<template>
  <el-dialog destroy-on-close :model-value="visible" title="创建短信模板" :width="840" @close="handleClose">
    <el-form ref="formRef" label-width="120px" :model="formData" :rules="formRules">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="模板签名" prop="signName">
            <el-select v-model="formData.signName" clearable placeholder="请选择签名">
              <el-option v-for="sign in signOptions" :key="sign" :label="sign" :value="sign" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="模板主题" prop="theme">
            <el-input v-model="formData.theme" clearable maxlength="50" placeholder="请输入主题" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="模板内容" prop="content">
        <el-input
          v-model="formData.content"
          maxlength="1000"
          placeholder="请输入模板内容"
          :rows="6"
          show-word-limit
          type="textarea"
        />
        <div class="hint">
          请检查短信内容按运营商规定添加“
          <span class="highlight">拒收请回复R</span>
          ”。
          <br />
          模板示例：很高兴通知您，您的报名申请已通过，拒收请回复R。
        </div>
      </el-form-item>

      <el-form-item label="引流报备">
        <el-switch v-model="flowReportEnabled" />
        <span class="hint" style="margin-left: 12px">
          应运营商要求，模板涉及到的引流信息（包含 URL/链接及联系方式）需提前报备，若无请忽略。
        </span>
      </el-form-item>

      <div v-if="flowReportEnabled" class="flow-report">
        <el-form-item label="报备类型">
          <el-button-group>
            <el-button :type="flowReportMode === 'link' ? 'primary' : 'default'" @click="flowReportMode = 'link'">
              链接报备
            </el-button>
            <el-button :type="flowReportMode === 'contact' ? 'primary' : 'default'" @click="flowReportMode = 'contact'">
              联系方式报备
            </el-button>
            <el-button :type="flowReportMode === 'both' ? 'primary' : 'default'" @click="flowReportMode = 'both'">
              链接+联系方式报备
            </el-button>
          </el-button-group>
        </el-form-item>

        <el-row :gutter="16">
          <el-col v-if="showLinkFields" :span="12">
            <el-form-item label="短信链接" prop="smsLink" :rules="smsLinkRules">
              <el-input v-model="formData.smsLink" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
          <el-col v-if="showLinkFields" :span="12">
            <el-form-item label="ICP 备案号" prop="icpNo" :rules="icpNoRules">
              <el-input v-model="formData.icpNo" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row v-if="showLinkFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="ICP 企业名称" prop="icpEnterprise" :rules="icpEnterpriseRules">
              <el-input v-model="formData.icpEnterprise" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="企业信用代码" prop="creditCode" :rules="creditCodeRules">
              <el-input v-model="formData.creditCode" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row v-if="showContactFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="号码类型" prop="reportNumberType" :rules="reportNumberTypeRules">
              <el-select v-model="formData.reportNumberType" clearable placeholder="请选择">
                <el-option v-for="(label, value) in PHONE_NUMBER_TYPE_CN" :key="value" :label="label" :value="value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="号码" prop="reportNumber" :rules="reportNumberRules">
              <el-input v-model="formData.reportNumber" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row v-if="showContactFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="所属人姓名" prop="reportOwnerName" :rules="reportOwnerNameRules">
              <el-input v-model="formData.reportOwnerName" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="证件类型" prop="reportCertType" :rules="reportCertTypeRules">
              <el-select v-model="formData.reportCertType" clearable placeholder="请选择">
                <el-option v-for="(label, value) in ID_CARD_TYPE_CN" :key="value" :label="label" :value="value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row v-if="showContactFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="证件号码" prop="reportCertNo" :rules="reportCertNoRules">
              <el-input v-model="formData.reportCertNo" clearable placeholder="请输入" />
            </el-form-item>
          </el-col>
        </el-row>
      </div>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="confirm">确定</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
  import { computed, ref, reactive, getCurrentInstance } from 'vue'
  import { createSmsTemplate } from '@/api/sms'
  import { ID_CARD_TYPE_CN, PHONE_NUMBER_TYPE_CN } from './context'

  const SIGN_OPTIONS = {
    WX_MINI_WEMEET: ['【上海荟神】', '【上海市人工智能行业协会】', '【钬花星球】', '【中国会展】', '【华商出海联盟】'],
    WX_MINI_MEETSG: ['【上海荟神】'],
    WX_MINI_MEETVGS: ['上海荟神'],
    WX_MINI_HSCH: ['【上海荟神】', '【华商出海联盟】'],
    WX_MINI_ZGHZ: ['【上海荟神】', '【中国会展】'],
    WX_MINI_HHXQ: ['【上海荟神】', '【钬花星球】'],
    WX_MINI_GDC: ['【上海荟神】', '【上海市人工智能行业协会】'],
    WX_MINI_YQ: ['【上海荟神】'],
  }

  const props = defineProps({
    visible: Boolean,
    appKey: {
      type: String,
      default: 'WX_MINI_WEMEET',
    },
  })
  const emit = defineEmits(['success', 'close'])

  const { proxy } = getCurrentInstance()

  const formRef = ref(null)

  const defaultFormData = {
    signName: '',
    theme: '',
    content: '',
    remark: '',

    flowReportEnabled: false,
    flowReportMode: 'link',

    smsLink: '',
    icpNo: '',
    icpEnterprise: '',
    creditCode: '',

    reportNumber: '',
    reportNumberType: '',
    reportOwnerName: '',
    reportCertType: '',
    reportCertNo: '',
  }

  const formData = reactive({ ...defaultFormData })

  const signOptions = computed(() => {
    return SIGN_OPTIONS[props.appKey] || Object.values(SIGN_OPTIONS).flat()
  })

  const flowReportMode = computed({
    get: () => formData.flowReportMode,
    set: (v) => {
      formData.flowReportMode = v
    },
  })

  const flowReportEnabled = computed({
    get: () => formData.flowReportEnabled,
    set: (v) => {
      formData.flowReportEnabled = v
    },
  })

  const showLinkFields = computed(() => flowReportEnabled.value && ['link', 'both'].includes(flowReportMode.value))
  const showContactFields = computed(
    () => flowReportEnabled.value && ['contact', 'both'].includes(flowReportMode.value)
  )

  const formRules = {
    signName: [{ required: true, message: '请选择模板签名', trigger: 'change' }],
    theme: [{ required: true, message: '请输入模板主题', trigger: 'blur' }],
    content: [{ required: true, message: '请输入模板内容', trigger: 'blur' }],
  }

  const smsLinkRules = computed(() => {
    if (!showLinkFields.value) return []
    return [{ required: true, message: '请输入短信链接', trigger: 'blur' }]
  })

  const icpNoRules = computed(() => {
    if (!showLinkFields.value) return []
    return [{ required: true, message: '请输入 ICP 备案号', trigger: 'blur' }]
  })

  const icpEnterpriseRules = computed(() => {
    if (!showLinkFields.value) return []
    return [{ required: true, message: '请输入 ICP 企业名称', trigger: 'blur' }]
  })

  const creditCodeRules = computed(() => {
    if (!showLinkFields.value) return []
    return [{ required: true, message: '请输入企业信用代码', trigger: 'blur' }]
  })

  const reportNumberRules = computed(() => {
    if (!showContactFields.value) return []
    return [{ required: true, message: '请输入号码', trigger: 'blur' }]
  })

  const reportNumberTypeRules = computed(() => {
    if (!showContactFields.value) return []
    return [{ required: true, message: '请选择号码类型', trigger: 'change' }]
  })

  const reportOwnerNameRules = computed(() => {
    if (!showContactFields.value) return []
    return [{ required: true, message: '请输入所属人姓名', trigger: 'blur' }]
  })

  const reportCertTypeRules = computed(() => {
    if (!showContactFields.value) return []
    return [{ required: true, message: '请选择证件类型', trigger: 'change' }]
  })

  const reportCertNoRules = computed(() => {
    if (!showContactFields.value) return []
    return [{ required: true, message: '请输入证件号码', trigger: 'blur' }]
  })

  function resetForm() {
    Object.assign(formData, defaultFormData)
    formRef.value?.clearValidate()
  }

  function handleClose() {
    emit('close')
    resetForm()
  }

  function confirm() {
    formRef.value.validate(async (valid) => {
      if (!valid) return

      const payload = {
        sign: formData.signName,
        theme: formData.theme,
        content: formData.content,
        remark: formData.remark,
        paramType: formData.paramType
          ? formData.paramType
              .split(/[\n,，]+/)
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        combinePhones: [],
        reportPhones: [],
        reportUrls: [],
        phoneReportInfo: {},
      }

      if (showLinkFields.value) {
        payload.reportUrls = [
          {
            url: formData.smsLink,
            ur1FilePic: [],
          },
        ]
      }

      if (showContactFields.value && formData.reportNumber) {
        payload.phoneReportInfo = {
          [formData.reportNumber]: {
            type: formData.reportNumberType || 0,
            belongName: formData.reportOwnerName || '',
            certificateType: formData.reportCertType || '',
            certificateNumber: formData.reportCertNo || '',
            unitName: '',
            phoneFilPic: [],
          },
        }
      }

      const res = await createSmsTemplate(payload)
      if (res.code !== 200) return

      proxy.$baseMessage('模板创建成功', 'success', 'vab-hey-message-success')
      emit('success')
      resetForm()
    })
  }
</script>

<style lang="scss" scoped>
  .hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
    margin-top: 6px;
  }

  .highlight {
    color: #ff4d4f;
  }

  .flow-report {
    margin-bottom: 16px;
  }
</style>
